<template>
	<section id="option-area" class="option-area">
		<!-- Loader -->
		<FullscreenLoading :loading="loading"/>

		<b-container>
			<!-- Header text -->
			<h2 :class="classForTitle()">
				<strong>{{ title }}</strong>
			</h2>

			<!-- Options row -->
			<b-row
				v-if="products && products.length > 0"
				class="justify-center"
			>
				<OfferOptionsCard
					v-for="n in products.length"
					:key="`card-${n - 1}-index-${selectedFlavors[n - 1]}`"
					:active-variation="selectedFlavors[n - 1]"
					:products="products"
					:product="products[n - 1]"
					:options="options"
					:send-offer="sendOffer"
					:customer-is-eligible-for-tax="customerIsEligibleForTax"
					@selectionUpdated="updateSelection"
				/>
			</b-row>

			<details
				v-if="showProductWarning"
				class="product-warning-container"
			>
				<summary>PRODUCT WARNINGS</summary>
				<p class="product-warning-text">
					<strong>⚠️ WARNING:</strong>This product can expose you to
					chemicals including lead, which is known to the State of
					California to cause cancer. For more information, go to
					<a href="https://www.P65Warnings.ca.gov"
					   target="_blank"
					>www.P65Warnings.ca.gov</a
					>
				</p>
			</details>
		</b-container>

		<ShippingAddressModal v-if="shouldShowModal" :offers="acceptedOffers"/>
	</section>
</template>

<script>
import Vue from 'vue';
import api from '@js/api';
import helpers from '@js/helpers';
import FullscreenLoading from '@component/UI/FullscreenLoading.vue';
import persistListener from '@js/mixins/persist-listener';
import OfferOptionsCard from './OfferOptionsCard.vue';
import ShippingAddressModal from './ShippingAddressModal.vue';
export default {
	components: {
		FullscreenLoading,
		OfferOptionsCard,
		ShippingAddressModal,
	},

	mixins: [persistListener],

	props: {
		offers: Array,
		options: Object,
	},

	data() {
		return {
			products: [],
			loading: false,
			selectedFlavors: [0, 0, 0],
			title: '',
			customerIsEligibleForTax: false,
			shouldShowModal: false,
			acceptedOffers: [],
		};
	},
	computed: {
		showProductWarning() {
			const { sku } = this.products[0][this.selectedFlavors[0]][0];
			return sku.startsWith('protein_chocolate') ||
				sku.startsWith('greens') ||
				sku.startsWith('turmeric_black') ||
				sku.startsWith('pre_workout') ||
				this.options?.productWarning;
		},
	},
	beforeMount() {
		this.title = this.options.offer_title
			? this.options.offer_title
			: 'Product Options';
		this.products = this.options.offers;
		this.customerIsEligibleForTax =
			this.customerIsEligibleForTax ||
			helpers.getCustomerEligibleForTax();
		this.filterProducts();
	},

	methods: {
		updateSelection(value) {
			this.products.forEach((product, cardNumber) => {
				product.forEach((option, dropDownIndex) => {
					if (option[0].flavor === value) {
						this.$set(
							this.selectedFlavors,
							cardNumber,
							dropDownIndex
						);
					}
				});
			});
		},

		handlePagePersisted() {
			this.loading = false;
		},

		filterProducts() {
			if (!this.products || !this.products[0]) return false;

			this.products.forEach((product) => {
				product.options.forEach((option) => {
					option.forEach((item) => {
						item.flavor = product.title;
						item.in_stock = product.in_stock;
						item.restock_date = product.restock_date;
						this.setProductOffer(item);
					}, this);
				}, this);
			});

			this.products = this.products.reduce((acc, { options }) => {
				options.forEach((option, cardNumber) => {
					if (option.length) {
						if (!acc[cardNumber]) {
							acc[cardNumber] = [option];
						} else {
							acc[cardNumber].push(option);
						}
					}
				});
				return acc;
			}, []);
		},

		setProductOffer(product) {
			this.$set(
				product,
				'offer',
				this.offers.filter((offer) => offer.sku === product.sku)[0]
			);
		},

		classForTitle() {
			return this.options.offer_title_class
				? this.options.offer_title_class
				: 'text-center red';
		},

		async sendOffer(orderOffer) {
			this.loading = true;
			switch (this.options.type) {
				case 'purchase':
					this.acceptedOffers = [orderOffer.offer.id];

					try {
						const { meta } = await api.acceptUpsell({ offers: this.acceptedOffers });
						window.location.href = meta.next_url;
					} catch (error) {
						this.loading = false;
						if (
							error.response.status === 422
							&& error.response.data.errors.hasOwnProperty('shippingAddress')
							&& error.response.data.errors.shippingAddress[0] === 'A shipping address is required.'
						) {
							this.shouldShowModal = true;
							return;
						} else if (
							error.response.status === 422
							&& error.response.data.errors.hasOwnProperty('product')
						) {
							Vue.swal({
								title: 'Failed to Purchase',
								text: error.response.data.errors.product[0],
								icon: 'error',
								confirmButtonColor: 'red'
							});
							return;
						}

						const checkoutRecoveryPath = helpers.getCheckoutRecoveryPath(this.acceptedOffers);
						error.checkoutRecoveryPath = checkoutRecoveryPath;
						this.$ErrorHandler.reportError(
							'User failed to accept upsell! Sending user to checkout recovery.',
							error
						);
						window.location.href = checkoutRecoveryPath;
					}
					break;

				case 'checkout':
					let url = orderOffer.redirect_target ? `${orderOffer.redirect_target}` : `/checkout/${orderOffer.offer.sku}`;
					if(!orderOffer.exclude_sku_from_url){
						url += `?offer_skus=${orderOffer.offer.sku}`;
					}
					if (orderOffer.funnel && orderOffer.funnel !== 'null') {
						if (url.includes('?')) {
							url += `&funnel=${orderOffer.funnel}`;
						} else {
							url += `?funnel=${orderOffer.funnel}`;
						}
					}

					window.location.href = url;
					break;

				default:
					try {
						await api.addOfferToCart({ id: orderOffer.offer.id });
						let url = orderOffer.redirect_target
							? orderOffer.redirect_target
							: '/cart';

						if (orderOffer.funnel && orderOffer.funnel !== 'null') {
							url += `?funnel=${orderOffer.funnel}`;
						}

						window.location.href = url;
					} catch (error) {
						this.$ErrorHandler.reportError(
							'User failed to add offer to cart!',
							error
						);
						this.loading = false;
					}
					break;
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.site-btn {
	color: #fff !important;
}

.single-option {
	height: auto !important;
}

.justify-center {
	justify-content: center;
}

.product-warning-container {
	margin: 2rem 0;
	border-top: 1px solid #d8d8d8;
	border-bottom: 1px solid #d8d8d8;
	padding: 0.5rem;
}

.product-warning-container summary {
	padding: 1rem;
	font-weight: 800;
	font-size: 1.8rem;
}

.product-warning-text {
	margin: 0.5rem;
}
</style>
